import axios from "axios";
import Config from "../Config";
import ErrorMessage from "../libs/helpers/ErrorMessage";
import { getFromLocalStorage } from "../libs/helpers/localstorage";

function agentAuthHeaders() {
  const ls = getFromLocalStorage();
  const tenant = getFromLocalStorage("lefacWebTenant")?.data;
  const token = ls && ls.token;
  return {
    Authorization: `Bearer ${token}`,
    "x-tenant": tenant,
  };
}

function rethrowAsErrorMessage(e) {
  if (e.response) {
    const { statusText, status } = e.response;
    const { httpStatus, clientType, description } = e.response.data;
    throw new ErrorMessage(
      statusText,
      description,
      httpStatus,
      status,
      clientType,
    );
  }
  throw e;
}

// Describes a Company Search filters object in natural language, for the
// filters-as-text summary shown above the search form.
export async function filtersToTextApi({ filters, companyNames, locale }) {
  try {
    const res = await axios.post(
      `${Config.apiBaseurl}/${Config.apis.agent.filtersToText}`,
      { filters, companyNames, locale },
      { headers: agentAuthHeaders() },
    );
    return res.data;
  } catch (e) {
    return rethrowAsErrorMessage(e);
  }
}

// Interprets a user's hand-edit of that description and applies whatever part
// of it maps to real filters, preserving anything the edit didn't touch.
export async function textToFiltersApi({
  filters,
  originalText,
  editedText,
  locale,
}) {
  try {
    const res = await axios.post(
      `${Config.apiBaseurl}/${Config.apis.agent.textToFilters}`,
      { filters, originalText, editedText, locale },
      { headers: agentAuthHeaders() },
    );
    return res.data;
  } catch (e) {
    return rethrowAsErrorMessage(e);
  }
}

// Bounds how long we'll wait for a single read() to produce more data. The
// backend sends a heartbeat line every 15s even when Claude itself has
// nothing new to report, so this should only ever fire if the connection is
// actually dead — otherwise a silently dropped stream leaves the UI spinning
// forever, since neither `done` nor an exception ever arrives on its own.
const CHUNK_IDLE_TIMEOUT_MS = 45000;

// Streams newline-delimited JSON events from the agent chat endpoint. Calls
// `onStatus(label)` for each live progress update ("Thinking…", "Using X…")
// and resolves with the final `{content, action, model, stopReason, usage}`
// payload once the stream ends.
export async function chatWithAgentApi(
  { message, history },
  onStatus = () => {},
) {
  const ls = getFromLocalStorage();
  const tenant = getFromLocalStorage("lefacWebTenant")?.data;
  const token = ls && ls.token;

  const res = await fetch(`${Config.apiBaseurl}/${Config.apis.agent.chat}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant": tenant,
    },
    body: JSON.stringify({ message, history }),
  });

  if (!res.ok) {
    let body;
    try {
      body = await res.json();
    } catch (e) {
      // response body wasn't JSON (or was empty) — fall through with no body
    }
    const err = new Error(
      body?.description ||
        body?.message ||
        `Request failed with status ${res.status}`,
    );
    err.response = { data: body };
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = null;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let idleTimer;
      const { done: streamDone, value } = await Promise.race([
        reader.read(),
        new Promise((_, reject) => {
          idleTimer = setTimeout(
            () =>
              reject(
                new Error("The agent stopped responding — please try again."),
              ),
            CHUNK_IDLE_TIMEOUT_MS,
          );
        }),
      ]);
      clearTimeout(idleTimer);
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf("\n");
      while (newlineIndex >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line) {
          const event = JSON.parse(line);
          if (event.type === "status") {
            onStatus(event.label);
          } else if (event.type === "error") {
            throw new Error(event.message || "Unexpected error");
          } else if (event.type === "done") {
            done = event;
          }
        }
        newlineIndex = buffer.indexOf("\n");
      }
    }
  } catch (e) {
    reader.cancel().catch(() => {});
    throw e;
  }

  return done;
}
